import React from "react"
import PropTypes from "prop-types"
import classNames from "classnames"
import MediaQuery from "react-responsive"

// Config
import PRConfig from "components/Stacker/config/ProductConfiguratorConfig"
import ToggleConfig from "components/Stacker/config/ToggleConfig"
import BreakPointConfig from "components/Stacker/constants/breakpoints"
import ThemeConfig from "components/Stacker/config/ThemeConfig"

// Utils
import { setPid } from "lib/navigation-util"

// Actions
import {
  setMetal,
  nextStep,
  setDesign,
  prevStep,
} from "components/Stacker/actions/ProductConfiguratorActions"
import { getProductDetails } from "components/Stacker/actions/ProductDetailActions"

// Components
import DropDown from "components/Stacker/components/DropDown/DropDown"
import Icon from "components/Stacker/components/Icon/Icon"

// Styles
import "./ProductConfigMenu.scss"

import { useSelector } from "react-redux"
import { useContext } from "react"
import { StackerContext } from "components/DYStackers/StackerContext"
import { useDispatch } from "react-redux"
import { useI18nContext } from "i18n/i18n-react"

function ProductConfigMenu({
  metal,
  motifCode,
  text,
  design,
  currentStep,
  icon,
  pinkyDesignDropdown,
  baseStore,
  isQuickView,
  swatch,
  theme,
  configProductSet,
}) {
  const dispatch = useDispatch()
  const { LL } = useI18nContext()
  const stackerContext = useContext(StackerContext)

  const { isExpanded, configTypeValue } = useSelector((state) => {
    return {
      isExpanded: state.toggle.PINKY_DESIGN_DROPDOWN_TOGGLE,
      configTypeValue: state.productConfig.configTypeValue,
    }
  })

  const setSwatch = (e) => {
    const { value } = e.currentTarget

    const data = {
      metal: value,
    }

    const selectedMetalProduct = configProductSet.filter(
      (product) => value === product.METAL_VALUE
    )
    const pid = selectedMetalProduct[0].PRODUCT_ID

    const selectedSwatch = Object.keys(swatch).length > 0 ? swatch[pid] : {}

    setPid(pid)
    dispatch(
      getProductDetails({
        args: {
          productId: pid,
          baseStore,
          selectedSwatch,
          isQuickView,
        },
        retailerId: stackerContext.retailerId,
      })
    )
    dispatch(setMetal(data))
    if (currentStep === 1) {
      dispatch(nextStep())
    }
  }

  const formattedDropDownItems = () => {
    const formattedOptions = PRConfig.DESIGN_VALUES.map((designVal, i) => {
      return {
        ...designVal,
        label: designVal,
      }
    })
    return formattedOptions
  }

  const displayPinkyRingLogo = (isNotMobileScreen) => {
    const sororObj = PRConfig.ICON_VALUES[0].sororities
    const sororMotifCodeStart = sororObj[0].code
    const sororMotifCodeEnd = sororObj[sororObj.length - 1].code
    const parseIntNum = 10

    if (
      parseInt(motifCode, parseIntNum) >= sororMotifCodeStart &&
      parseInt(motifCode, parseIntNum) <= sororMotifCodeEnd
    ) {
      return (
        <div
          className={classNames({
            "d-flex align-items-center justify-content-center ml-auto":
              isNotMobileScreen,
          })}
        >
          <Icon
            additionalClassNames="pinky-config-menu__options-group-icon"
            iconName="icon-alp-logo"
          />
        </div>
      )
    }

    return null
  }

  const handleSelection = (designObj) => {
    const data = {
      design: designObj.label,
    }
    dispatch(setDesign(data))
    if (currentStep === 2) {
      dispatch(nextStep())
    } else if (currentStep === 4) {
      dispatch(prevStep())
    }
  }

  let selectedItem
  if (design) {
    selectedItem = design
  }

  const isMensPinkyRing =
    theme === ThemeConfig.MEN &&
    configTypeValue === PRConfig.PINKY_PRODUCTS.PRODUCT_TYPE
  const isThirdStep =
    icon && currentStep > 2 && design === PRConfig.DESIGN_VALUES[1]

  return (
    <div className="ProductConfigMenu pinky-config-menu container-fluid">
      <div className="pinky-config-menu__wrapper d-flex justify-content-center justify-content-xl-start">
        {/* Metal selections */}
        <div className="pinky-config-menu__options-group d-flex align-items-center justify-content-center">
          <h6 className="pinky-config-menu__options-group-title">
            {LL.productConfig.metalLabel()}:
          </h6>
          <div className="pinky-config-menu__options-group-items d-flex align-items-center justify-content-center">
            {configProductSet.map((metalVal, i) => (
              <button
                key={`${metalVal.METAL_VALUE}-key`}
                value={metalVal.METAL_VALUE}
                onClick={setSwatch}
                className={classNames("swatch-option hover-transition", {
                  selected: metal === metalVal.METAL_VALUE,
                })}
              >
                <img
                  src={`https://img.davidyurman.com/is/image/DavidYurman/pinky-ring-swatch-${metalVal.METAL_VALUE}?fmt=png-alpha`}
                  alt={metalVal.METAL_VALUE}
                />
              </button>
            ))}
          </div>
        </div>

        {/* design selections */}
        {currentStep > 1 && !isMensPinkyRing && (
          <div className="pinky-config-menu__options-group pinky-config-menu__options-group-dropdown d-flex align-items-center justify-content-center">
            <div
              id="designDropdown"
              className={classNames("pinky-config-menu__options-group-items", {
                expanded: isExpanded,
              })}
            >
              <DropDown
                id={ToggleConfig.PINKY_DESIGN_DROPDOWN_TOGGLE}
                selectedItem={
                  selectedItem || LL.productConfig.selectDesignDropdownText()
                }
                items={formattedDropDownItems()}
                itemAction={handleSelection}
                showDropDown={pinkyDesignDropdown}
                isNotFormComponent
              />
            </div>
          </div>
        )}

        {design && currentStep > 1 && isMensPinkyRing && (
          <div className="pinky-config-menu__options-group pinky-config-menu__options-group d-flex align-items-center justify-content-center text-uppercase">
            <div className="pinky-config-menu__options-group-items">
              <span>{design}</span>
            </div>
          </div>
        )}

        {/* custom selections */}
        {text && currentStep > 2 && design === PRConfig.DESIGN_VALUES[0] && (
          <div className="pinky-config-menu__options-group pinky-config-menu__options-group d-flex align-items-center justify-content-center text-uppercase">
            <div className="pinky-config-menu__options-group-items">
              <span>{text}</span>
            </div>
          </div>
        )}

        {isThirdStep && (
          <div className="pinky-config-menu__options-group pinky-config-menu__options-group d-flex align-items-center justify-content-center text-capitalize">
            <div className="pinky-config-menu__options-group-items text-center">
              <MediaQuery query={BreakPointConfig.IS_MOBILE}>
                {displayPinkyRingLogo(false)}
              </MediaQuery>
              <span>{icon.split("-").join(" ")}</span>
            </div>
          </div>
        )}

        {isThirdStep && (
          <MediaQuery query={BreakPointConfig.IS_DESKTOP}>
            {displayPinkyRingLogo(true)}
          </MediaQuery>
        )}
      </div>
    </div>
  )
}

ProductConfigMenu.propTypes = {
  metal: PropTypes.string,
  motifCode: PropTypes.string,
  text: PropTypes.string,
  design: PropTypes.string,
  currentStep: PropTypes.number.isRequired,
  icon: PropTypes.string,
  pinkyDesignDropdown: PropTypes.bool,
  isExpanded: PropTypes.bool,
  baseStore: PropTypes.string,
  isQuickView: PropTypes.bool,
  swatch: PropTypes.object,
  theme: PropTypes.string,
  configProductSet: PropTypes.array,
  configTypeValue: PropTypes.string,
}

export default ProductConfigMenu
